import { HandIcon, MoreHorizontalIcon, TextIcon } from 'lucide-react';
import { useState } from 'react';

import type { AccountDTO } from '@invenco/accounts-interface';

import {
  AlertDialogWithAction,
  AlertDialogWithActionCancel,
  AlertDialogWithActionContent,
  AlertDialogWithActionDescription,
  AlertDialogWithActionFooter,
  AlertDialogWithActionHeader,
  AlertDialogWithActionSubmit,
  AlertDialogWithActionTitle,
} from '@/components/alert-dialog-with-action';
import { Button } from '@/components/core/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/core/dropdown-menu';
import { DetailList, type DetailListItem } from '@/components/detail-list';
import { FormDialog } from '@/components/forms/form-dialog';
import { StatusBadge } from '@/components/status-badge';
import { useGateways } from '@/gateways/GatewayProvider';
import { Result } from '@/shared/helpers/Result';
import { useGatewayMutation } from '@/shared/hooks/queries';

import { BillingHoldDialogContent } from './billing-hold-dialog-content';

type Props = {
  account: Partial<AccountDTO>;
  isLoading?: boolean;
};

/**
 * Right-rail "Billing" section for the Account Detail page.
 *
 * The account billing hold is a COMMAND (apply / release), not a field on the
 * account Edit form - so it gets its own affordance (a kebab menu) that drives
 * the `enable_bill_holds` / `disable_bill_holds` account commands, mirroring how
 * bill holds are added and released on the Bill Detail page. Both commands
 * return the updated account, so the mutation's `linkedQuery` writes it straight
 * into the cache the Account Detail page reads - no manual refetch needed.
 */
export function BillingHoldDetail({ account, isLoading }: Props) {
  const { accountsGateway } = useGateways();
  const accountId = account.id;
  const onHold = account.billHoldsEnabled ?? false;
  const reason = account.billHoldsReason ?? null;

  const [isApplyOpen, setIsApplyOpen] = useState(false);
  const [isReleaseOpen, setIsReleaseOpen] = useState(false);

  const { mutate: enableBillHolds } = useGatewayMutation<AccountDTO, string>({
    mutationFn: (nextReason: string) => accountsGateway.enableBillHolds(accountId!, nextReason),
    linkedQuery: ['accounts', accountId],
    successMessage: 'Billing hold applied',
  });

  const { mutate: disableBillHolds } = useGatewayMutation<AccountDTO, void>({
    mutationFn: () => accountsGateway.disableBillHolds(accountId!),
    linkedQuery: ['accounts', accountId],
    successMessage: 'Billing hold released',
  });

  const items: DetailListItem[] = [
    {
      icon: HandIcon,
      label: 'Auto bills hold',
      value: onHold ? (
        <StatusBadge type="alert">Enabled</StatusBadge>
      ) : (
        <StatusBadge type="inactive">Disabled</StatusBadge>
      ),
    },
    ...(onHold && reason ? [{ icon: TextIcon, label: 'Reason', value: reason }] : []),
  ];

  return (
    <>
      <DetailList
        title="Billing"
        isLoading={isLoading}
        action={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Billing hold actions"
                disabled={isLoading || !accountId}
                className="-mr-1.5"
              >
                <MoreHorizontalIcon />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {onHold ? (
                <DropdownMenuItem variant="destructive" onSelect={() => setIsReleaseOpen(true)}>
                  Release hold
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onSelect={() => setIsApplyOpen(true)}>
                  Apply hold
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        }
        items={items}
      />

      <FormDialog open={isApplyOpen} onOpenChange={setIsApplyOpen}>
        <BillingHoldDialogContent
          onApply={(nextReason) => {
            if (!accountId) return Promise.resolve(Result.fail('Account ID missing'));
            return enableBillHolds(nextReason);
          }}
        />
      </FormDialog>

      <AlertDialogWithAction
        open={isReleaseOpen}
        onOpenChange={setIsReleaseOpen}
        onAction={() => {
          if (!accountId) return Promise.resolve(Result.fail('Account ID missing'));
          return disableBillHolds();
        }}
      >
        <AlertDialogWithActionContent>
          <AlertDialogWithActionHeader>
            <AlertDialogWithActionTitle>Release billing hold?</AlertDialogWithActionTitle>
            <AlertDialogWithActionDescription>
              New bills for this account will no longer be automatically held. Bills already on hold
              are unaffected and must be released individually.
            </AlertDialogWithActionDescription>
          </AlertDialogWithActionHeader>
          <AlertDialogWithActionFooter>
            <AlertDialogWithActionCancel>Cancel</AlertDialogWithActionCancel>
            <AlertDialogWithActionSubmit>Release</AlertDialogWithActionSubmit>
          </AlertDialogWithActionFooter>
        </AlertDialogWithActionContent>
      </AlertDialogWithAction>
    </>
  );
}
